import { useSourceAndTargetCurrenciesCommon } from '@eih-netbroker/common';
import { useContext, useMemo } from 'react';
import { useSnackbar } from '../../context/SnackbarContext';
import { CurrencyExchangeSourceAndTargetCurrencyManagerContext } from '../../modules/core/components/cash-popover/CurrencyExchangeSourceAndTargetCurrencyManagerContext';
import { Config } from '../config/config';
import { getClientNormalAccountId } from '../utils/accountUtils';
import { useAccountBalance } from './useAccountBalance';
import { useAccounts } from './useAccounts';

const haveNightPeriods = Config.NetBroker.CurrencyExchange.HaveNightPeriods;
type UseSourceAndTargetCurrenciesCommonParams = Parameters<typeof useSourceAndTargetCurrenciesCommon>[0];

export const useSourceAndTargetCurrencies = () => {
  const currencyExchangeSourceAndTargetCurrencyManagerContext = useContext(
    CurrencyExchangeSourceAndTargetCurrencyManagerContext,
  );
  if (!currencyExchangeSourceAndTargetCurrencyManagerContext) {
    throw new Error('StandaloneCurrencyExchangeSourceAndTargetCurrencyManager context is not provided');
  }
  const { currencyExchangeEvents } = currencyExchangeSourceAndTargetCurrencyManagerContext;
  const { showError } = useSnackbar();
  const { getCashInfoByCurrency } = useAccountBalance();

  const { selectedAccountOrClient, isMultipleAccountSelected, selectedClient } = useAccounts();
  const selectedClientNormalAccountId = useMemo(() => getClientNormalAccountId(selectedClient), [selectedClient]);
  const shouldLoadSourceCurrencies = useMemo(
    () => !isMultipleAccountSelected || !!selectedClientNormalAccountId,
    [isMultipleAccountSelected, selectedClientNormalAccountId],
  );
  const params = useMemo<UseSourceAndTargetCurrenciesCommonParams>(
    () => ({
      currencyExchangeEvents,
      shouldLoadSourceCurrencies,
      cashInfoByCurrency: getCashInfoByCurrency(selectedAccountOrClient),
      showError: selectedAccountOrClient.client ? () => {} : showError, // if no account is selected, we don't want to show error messages
      haveNightPeriods,
    }),
    [currencyExchangeEvents, getCashInfoByCurrency, shouldLoadSourceCurrencies, selectedAccountOrClient, showError],
  );

  return useSourceAndTargetCurrenciesCommon(params);
};
